import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScanLine, X, CornerDownLeft } from 'lucide-react';
import { scanApi } from '../services/api';

/**
 * Универсальный поиск сканером: один код — сразу нужный экран.
 *
 * Набор номера руками — самая долгая операция в смене: сотрудник помнит, какой
 * номер куда вбивается, ошибается в цифре, переходит по вкладкам. Здесь он
 * подносит сканер к любому коду — ШК товара, коробу приёмки, ячейке, грузоместу,
 * ЧЗ, номеру заказа или поставки — и попадает туда, где с этим работают.
 *
 * Открывается по F2 или по кнопке в меню. Сканер сам дожимает Enter, поэтому
 * поле обрабатывает submit, а не «поиск по мере ввода» — иначе на длинном ЧЗ
 * ушло бы 30 запросов подряд.
 */
export default function GlobalScan() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      // F2 — свободная клавиша на всех страницах; Esc закрывает
      if (e.key === 'F2') { e.preventDefault(); setOpen(true); }
      else if (e.key === 'Escape' && open) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (open) {
      setCode(''); setResult(null); setError('');
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  const submit = useCallback(async (e) => {
    e?.preventDefault?.();
    const v = code.trim();
    if (!v || busy) return;
    setBusy(true); setError(''); setResult(null);
    try {
      const r = await scanApi.resolve(v);
      const d = r.data || {};
      if (!d.found) {
        setError(d.detail || 'Код не опознан');
        setCode('');
        inputRef.current?.focus();
        return;
      }
      setResult(d);
      // Переходим сразу: сотрудник сканирует и работает, лишний клик не нужен.
      setOpen(false);
      navigate(d.route);
    } catch (err) {
      setError(err.response?.data?.detail || err.friendlyMessage || 'Ошибка поиска');
      setCode('');
      inputRef.current?.focus();
    } finally {
      setBusy(false);
    }
  }, [code, busy, navigate]);

  if (!open) {
    return (
      <button className="btn-theme-toggle" onClick={() => setOpen(true)} title="Поиск сканером (F2)">
        <ScanLine size={16} />
      </button>
    );
  }

  return (
    <div
      onClick={() => setOpen(false)}
      style={{
        position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(15,23,42,0.45)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '14vh',
      }}
    >
      <form
        onClick={e => e.stopPropagation()}
        onSubmit={submit}
        style={{
          width: 'min(560px, 92vw)', background: 'var(--bg-card)', borderRadius: 16,
          border: '1px solid var(--border)', boxShadow: '0 24px 60px rgba(15,23,42,0.28)',
          overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
          <ScanLine size={20} style={{ color: 'var(--primary, #2563eb)' }} />
          <b style={{ fontSize: '0.95rem' }}>Поиск сканером</b>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>F2</span>
          <button type="button" className="btn btn-icon" style={{ marginLeft: 'auto' }} onClick={() => setOpen(false)}>
            <X size={16} />
          </button>
        </div>
        <div style={{ padding: 16 }}>
          <input
            ref={inputRef}
            value={code}
            onChange={e => setCode(e.target.value)}
            placeholder="Сканируйте код или введите номер…"
            style={{
              width: '100%', padding: '12px 14px', fontSize: '1.05rem',
              borderRadius: 10, border: '1px solid var(--border)',
              background: 'var(--bg-input, var(--bg-card))', color: 'var(--text-primary)',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            }}
          />
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>
            Понимает: ШК товара и артикул · код ЧЗ · короб приёмки (BOX-…) · ячейку ·
            короб хранения · грузоместо · номер заказа, поставки и приёмки.
          </div>
          {error && (
            <div style={{ marginTop: 10, fontSize: 13, color: 'var(--danger)' }}>{error}</div>
          )}
          {result && (
            <div style={{ marginTop: 10, fontSize: 13 }}>
              <b>{result.title}</b>{result.subtitle ? ` · ${result.subtitle}` : ''}
            </div>
          )}
          <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-muted)' }}>
            <CornerDownLeft size={13} /> Enter — перейти {busy && '· ищу…'}
          </div>
        </div>
      </form>
    </div>
  );
}
